import type { Team } from 'api-client';

import { isAccessPausedPath } from '@/lib/accessPaused';
import { isBillingRequiredPath } from '@/lib/billingRequired';
import { logger } from '@/lib/logger';
import { parseOrgRoute } from '@/lib/prefetch/parseOrgRoute';

import { getPersistedAvailableOrgs } from './persistedAvailableOrgs';

export interface InitialHydration {
  orgs: Team[];
  currentOrg: Team | null;
  source: 'cache' | 'fetch';
}

function selectByMostRecentLastLogin(orgs: Team[]): Team | null {
  let best: Team | null = null;
  let bestTs = -Infinity;
  let tie = false;
  for (const o of orgs) {
    const raw = o.currentUserMembership?.lastLogin;
    const ts = raw ? Date.parse(raw) : NaN;
    if (Number.isNaN(ts)) {
      continue;
    }
    if (ts > bestTs) {
      bestTs = ts;
      best = o;
      tie = false;
    } else if (ts === bestTs) {
      tie = true;
    }
  }
  return tie ? null : best;
}

export function selectInitialCurrentOrg(orgs: Team[]): Team | null {
  // On the access-paused or billing-required gate routes, never select an org.
  // Org-scoped hooks in TeamProvider gate on currentOrg?.id; holding a gated
  // org here would fire 403-generating fetches and (combined with the redirect)
  // loop forever. These pages read orgId from URL params, not context.
  if (
    isAccessPausedPath(window.location.pathname) ||
    isBillingRequiredPath(window.location.pathname)
  ) {
    return null;
  }
  // 1. URL match
  const route = parseOrgRoute(window.location.pathname);
  if (route) {
    const fromUrl = orgs.find(o => o.id === route.orgId);
    if (fromUrl) {
      return fromUrl;
    }
  }
  // 2. Single-org auto-select
  if (orgs.length === 1) {
    return orgs[0];
  }
  // 3. Most recent membership last_login (backend signal)
  const byLastLogin = selectByMostRecentLastLogin(orgs);
  if (byLastLogin) {
    return byLastLogin;
  }
  // 4. Multi-org without a signal -> null (full-page picker)
  return null;
}

export function computeInitialHydration(
  userId: number | undefined
): InitialHydration {
  const FETCH_RESULT: InitialHydration = {
    orgs: [],
    currentOrg: null,
    source: 'fetch',
  };
  if (userId === undefined) {
    return FETCH_RESULT;
  }
  const cached = getPersistedAvailableOrgs(userId);
  if (!cached) {
    return FETCH_RESULT;
  }
  // useCurrentUserEntity.ts reads currentOrg.currentUserMembership.entityId
  // (primary) and falls back to scanning the members array. Reject caches
  // that lack the new currentUserMembership field OR were written for a
  // different user (shared-device protection). Empty members[] is valid
  // post-slim. entityId === null is allowed (user's CRM entity may be
  // pending — backend get_user_entity_id can legitimately return None).
  const allHaveMembers = cached.every(o => Array.isArray(o.members));
  if (!allHaveMembers) {
    logger.warn(
      'Cached availableOrgs missing members array, rejecting hydration',
      {
        userId,
        component: 'org-context',
        reason: 'members-array-invalid',
      }
    );
    return FETCH_RESULT;
  }
  const allHaveValidCurrentUserMembership = cached.every(o => {
    const cum = o.currentUserMembership;
    if (cum == null) {
      return false;
    }
    // userId must match the calling user — defends against stale cache
    // reuse on shared devices (login user A → cache → login user B).
    return cum.userId === userId;
  });
  if (!allHaveValidCurrentUserMembership) {
    logger.warn(
      'Cached availableOrgs missing or mismatched currentUserMembership, rejecting hydration',
      {
        userId,
        orgCount: cached.length,
        orgIds: cached.map(o => o.id),
        reason: 'currentUserMembership-invalid',
        component: 'org-context',
      }
    );
    return FETCH_RESULT;
  }
  // Reject caches written before isPayingCustomer shipped — the Ahoy Footer
  // toggle's lock state fails closed on a missing/undefined value, so a
  // stale cache without this field must not hydrate as "unlocked."
  const allHavePayingCustomerFlag = cached.every(
    o => typeof o.isPayingCustomer === 'boolean'
  );
  if (!allHavePayingCustomerFlag) {
    logger.warn(
      'Cached availableOrgs missing isPayingCustomer, rejecting hydration',
      {
        userId,
        orgCount: cached.length,
        orgIds: cached.map(o => o.id),
        reason: 'isPayingCustomer-missing',
        component: 'org-context',
      }
    );
    return FETCH_RESULT;
  }
  // Reject caches written before footerGateEnforced shipped — once the paid
  // gate is enforced, a stale cache without this field would hydrate the
  // footer toggle as "unlocked" (isFooterLocked treats a missing flag as
  // observe-only) until the next fetch. One-time rejection forces a fresh
  // fetch instead, same pattern as the isPayingCustomer check above.
  const allHaveFooterGateFlag = cached.every(
    o => typeof o.footerGateEnforced === 'boolean'
  );
  if (!allHaveFooterGateFlag) {
    logger.warn(
      'Cached availableOrgs missing footerGateEnforced, rejecting hydration',
      {
        userId,
        orgCount: cached.length,
        orgIds: cached.map(o => o.id),
        reason: 'footerGateEnforced-missing',
        component: 'org-context',
      }
    );
    return FETCH_RESULT;
  }
  return {
    orgs: cached,
    currentOrg: selectInitialCurrentOrg(cached),
    source: 'cache',
  };
}
